import { Handle, NodeProps, Position, useReactFlow } from '@xyflow/react'
import {
  getSymbolById,
  SYMBOL_SIZES,
} from 'features/processFlowDiagrams/data/minimalSymbols'
import { PfdNode } from 'features/processFlowDiagrams/types'
import React from 'react'
import styled from 'styled-components'

import { BaseNodeWrapper } from './BaseNodeWrapper'

const SymbolContainer = styled.div<{ width: string; height: string }>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SymbolSvg = styled.svg`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.forecast.text1};

  path,
  circle,
  ellipse,
  line,
  rect {
    stroke: currentColor;
  }
`

// Handle position mapping
const HANDLE_POSITIONS = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
}

// Handle type logic based on position
const getHandleType = (position: string): 'source' | 'target' => {
  // Inputs typically come from left/top, outputs go to right/bottom
  return position === 'left' || position === 'top' ? 'target' : 'source'
}

export const UniversalSymbolNode: React.FC<
  NodeProps<PfdNode> & { symbolId: string }
> = ({ symbolId, id, data }) => {
  const symbol = getSymbolById(symbolId)
  const { setNodes } = useReactFlow<PfdNode>()

  if (!symbol) {
    return (
      <BaseNodeWrapper id={id} link={data.link} setNodes={setNodes}>
        <SymbolContainer width='3rem' height='3rem'>
          <div>Symbol not found: {symbolId}</div>
        </SymbolContainer>
      </BaseNodeWrapper>
    )
  }

  const size = SYMBOL_SIZES[symbol.size]

  return (
    <BaseNodeWrapper id={id} link={data.link} setNodes={setNodes}>
      <SymbolContainer width={size.width} height={size.height}>
        {symbol.handles.map((handlePos, index) => {
          const handlesAtPosition = symbol.handles.filter(
            (h) => h === handlePos
          ).length
          const handleId =
            handlesAtPosition === 1 ? handlePos : `${handlePos}-${index}`

          return (
            <Handle
              key={`${handlePos}-${index}`}
              type={getHandleType(handlePos)}
              position={
                HANDLE_POSITIONS[handlePos as keyof typeof HANDLE_POSITIONS]
              }
              id={handleId}
            />
          )
        })}

        <SymbolSvg viewBox={symbol.viewBox}>{symbol.svgContent}</SymbolSvg>
      </SymbolContainer>
    </BaseNodeWrapper>
  )
}

// Universal preview component
export const UniversalSymbolPreview: React.FC<{
  symbolId: string
  scale?: number
}> = ({ symbolId, scale = 0.7 }) => {
  const symbol = getSymbolById(symbolId)

  if (!symbol) return <div>Symbol not found</div>

  const size = SYMBOL_SIZES[symbol.size]

  return (
    <SymbolContainer
      width={size.width}
      height={size.height}
      style={{ transform: `scale(${scale})` }}>
      <SymbolSvg viewBox={symbol.viewBox}>{symbol.svgContent}</SymbolSvg>
    </SymbolContainer>
  )
}
